/**
 * InlineEditableText Component
 *
 * A click-to-edit text field with validation, save on blur/enter, cancel on escape.
 * Supports single-line (TextInput) and multiline (Textarea) modes with text style variants.
 *
 * @example
 * ```tsx
 * // Basic usage - single line title
 * <InlineEditableText
 *   value={task.title}
 *   onSave={async (newTitle) => await updateTask({ title: newTitle })}
 *   textStyle="title"
 *   placeholder="Enter title..."
 *   required
 * />
 *
 * // Multiline with validation
 * <InlineEditableText
 *   value={task.description}
 *   onSave={async (newDesc) => await updateTask({ description: newDesc })}
 *   textStyle="body"
 *   multiline
 *   rows={4}
 *   maxLength={500}
 *   emptyText="No description"
 *   validate={(value) => value.length < 10 ? 'Minimum 10 characters' : null}
 * />
 *
 * // Subtitle with custom placeholder
 * <InlineEditableText
 *   value={workspace.name}
 *   onSave={handleSave}
 *   textStyle="subtitle"
 *   placeholder="Workspace name"
 *   emptyText="Unnamed workspace"
 * />
 * ```
 */

import type { KeyboardEvent, FocusEvent, ChangeEvent, RefObject } from 'react';
import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { TextInput, Textarea, Loader } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import type {
  InlineEditableTextProps,
  InlineEditableTextStyle,
} from './InlineEditableText.types';
import styles from './InlineEditableText.module.css';

/** Icon size for edit pencil */
const EDIT_ICON_SIZE = 14;

/** Loader size during save operation */
const LOADER_SIZE = 'xs';

/** Default rows for multiline mode */
const DEFAULT_MULTILINE_ROWS = 3;

/** CSS class mapping for text style variants */
const TEXT_STYLE_CLASSES: Record<InlineEditableTextStyle, string> = {
  title: 'styleTitle',
  subtitle: 'styleSubtitle',
  body: 'styleBody',
};

/** CSS class mapping for input text style variants */
const INPUT_STYLE_CLASSES: Record<InlineEditableTextStyle, string> = {
  title: 'inputTitle',
  subtitle: 'inputSubtitle',
  body: 'inputBody',
};

export const InlineEditableText = memo(function InlineEditableText({
  value,
  onSave,
  validate,
  required = false,
  maxLength,
  placeholder,
  emptyText = 'Empty',
  multiline = false,
  rows = DEFAULT_MULTILINE_ROWS,
  textStyle = 'body',
  readOnly = false,
  className,
}: InlineEditableTextProps) {
  // Component state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref for input focus
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Sync edit value when external value changes (while not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  /**
   * Enter edit mode
   */
  const handleStartEditing = useCallback(() => {
    if (readOnly) return;
    if (isLoading) return;
    setEditValue(value);
    setError(null);
    setIsEditing(true);
  }, [value, isLoading, readOnly]);

  /**
   * Cancel editing and restore original value
   */
  const handleCancel = useCallback(() => {
    setEditValue(value);
    setError(null);
    setIsEditing(false);
  }, [value]);

  /**
   * Validate and save the current value
   */
  const handleSave = useCallback(async () => {
    const trimmedValue = editValue.trim();

    // Required validation
    if (required && !trimmedValue) {
      setError('This field is required');
      return;
    }

    // Custom validation
    if (validate) {
      const validationError = validate(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Skip save if value unchanged
    if (trimmedValue === value) {
      setIsEditing(false);
      setError(null);
      return;
    }

    // Perform async save
    setIsLoading(true);
    setError(null);

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (saveError) {
      const errorMessage =
        saveError instanceof Error ? saveError.message : 'Failed to save';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [editValue, value, required, validate, onSave]);

  /**
   * Handle input change
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      // Enforce maxLength
      if (maxLength && newValue.length > maxLength) {
        return;
      }
      setEditValue(newValue);
      // Clear error on change
      if (error) {
        setError(null);
      }
    },
    [maxLength, error]
  );

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      } else if (event.key === 'Enter' && !multiline) {
        event.preventDefault();
        handleSave();
      }
    },
    [multiline, handleCancel, handleSave]
  );

  /**
   * Handle blur - save on blur
   */
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Prevent save if clicking inside the same component
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
        return;
      }
      handleSave();
    },
    [handleSave]
  );

  /**
   * Handle display click/keyboard to enter edit mode
   */
  const handleDisplayKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleStartEditing();
      }
    },
    [handleStartEditing]
  );

  // Build class names
  const textStyleClass = styles[TEXT_STYLE_CLASSES[textStyle]];
  const inputStyleClass = styles[INPUT_STYLE_CLASSES[textStyle]];
  const containerClass = [styles.container, className]
    .filter(Boolean)
    .join(' ');

  // Display mode
  if (!isEditing) {
    const isEmpty = !value.trim();
    const displayTextClass = [
      styles.displayText,
      textStyleClass,
      multiline && styles.displayTextMultiline,
      isEmpty && styles.emptyText,
    ]
      .filter(Boolean)
      .join(' ');

    if (readOnly) {
      return (
        <div className={containerClass}>
          <div className={styles.displayWrapperReadOnly}>
            <span className={displayTextClass}>
              {isEmpty ? emptyText : value}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={containerClass}>
        <div
          className={styles.displayWrapper}
          onClick={handleStartEditing}
          onKeyDown={handleDisplayKeyDown}
          role="button"
          tabIndex={0}
          aria-label={isEmpty ? emptyText : value}
        >
          <span className={displayTextClass}>
            {isEmpty ? emptyText : value}
          </span>
          <IconPencil size={EDIT_ICON_SIZE} className={styles.editIcon} />
        </div>
      </div>
    );
  }

  // Edit mode - build input class
  const inputClass = [
    styles.input,
    inputStyleClass,
    isLoading && styles.inputLoading,
    error && styles.inputError,
  ]
    .filter(Boolean)
    .join(' ');

  // Common input props
  const inputProps = {
    ref: inputRef as RefObject<HTMLInputElement> &
      RefObject<HTMLTextAreaElement>,
    value: editValue,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    placeholder,
    maxLength,
    disabled: isLoading,
    className: inputClass,
    'aria-invalid': !!error,
    'aria-describedby': error ? 'inline-edit-error' : undefined,
  };

  return (
    <div className={containerClass}>
      <div className={styles.editWrapper}>
        {multiline ? (
          <Textarea
            {...inputProps}
            ref={inputRef as RefObject<HTMLTextAreaElement>}
            rows={rows}
            autosize={false}
          />
        ) : (
          <TextInput
            {...inputProps}
            ref={inputRef as RefObject<HTMLInputElement>}
          />
        )}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <Loader size={LOADER_SIZE} />
          </div>
        )}
      </div>
      {error && (
        <div
          id="inline-edit-error"
          className={styles.errorMessage}
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
});

InlineEditableText.displayName = 'InlineEditableText';
